import { LOADER_PANEL_HEIGHT, LOADER_PANEL_WIDTH, LOADER_PHONE_WIDTH } from './constants'
import { setElementStyle } from './helpers'
import type { LoaderSide, LoaderTheme } from './types'
import { isFrameMessage, TAG, type LoaderMessage } from '../protocol'

/**
 * `chat.js` — the only Raptora code that runs on a customer's own page.
 */

/**
 * Where the frame document lives.
 */
const FRAME_ORIGIN = process.env.RAPTORA_WIDGET_URL ?? 'https://widget.raptora.io'
const API = process.env.RAPTORA_API_URL || FRAME_ORIGIN

/** Marks the host document so a second copy of the snippet is a no-op. */
const INSTALLED = 'data-raptora-widget'

/** How far the launcher sits from the corner. A constant here and in the preview. */
const EDGE = 20

/**
 * The panel, at the size the console previews it: 416×704.
 */
const PANEL_WIDTH = LOADER_PANEL_WIDTH
const PANEL_HEIGHT = LOADER_PANEL_HEIGHT

/**
 * Below this the panel is a sheet, not a card.
 */
const PHONE = LOADER_PHONE_WIDTH

type Config = {
  publishableKey: string
  agentId: string | null
  /** Where the launcher sits. Mirrored into the frame so the panel aligns. */
  side: LoaderSide
  /**
   * `data-user-id` — who the customer's own site says is logged in.
   */
  userId: string | null
}

/**
 * The half of `WidgetConfig` that describes things outside the frame.
 */
type Presentation = {
  accent?: string
  theme?: LoaderTheme
  radius?: number
  logo?: string | null
  launcher?: 'icon' | 'labeled'
  launcherLabel?: string
  teaser?: string
  autoOpen?: 'never' | 'delay' | 'exit'
  autoOpenDelaySec?: number
}

/**
 * Configuration comes off the script tag, not a global.
 */
const readConfig = (): Config | null => {
  const tag = document.currentScript as HTMLScriptElement | null
  if (!tag) return null

  const publishableKey = tag.getAttribute('data-key')
  if (!publishableKey) {
    // Console rather than throw: a misconfigured widget must not break the
    // page it is embedded in. The customer sees this while installing; the
    // visitor sees nothing at all, which is the correct failure.
    console.error('[raptora] the embed snippet is missing data-key')
    return null
  }

  return {
    publishableKey,
    agentId: tag.getAttribute('data-agent'),
    side: tag.getAttribute('data-side') === 'left' ? 'left' : 'right',
    userId: tag.getAttribute('data-user-id'),
  }
}

const style = setElementStyle

/**
 * Black or white on the accent, whichever a person can actually read.
 */
const readableOn = (hex: string): string => {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return '#ffffff'
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5)
  return luminance > 0.55 ? '#0b0b0c' : '#ffffff'
}

/**
 * The launcher's colour before the customer's arrives — and after, if none ever
 * does.
 */
const DEFAULT_ACCENT = '#e5e5ea'
const DEFAULT_ACCENT_INK = readableOn(DEFAULT_ACCENT)

/** The panel's own background, so the iframe's corners are not a white flash. */
const surfaceFor = (theme: Presentation['theme']): string => {
  const dark =
    theme === 'dark' ||
    (theme !== 'light' && window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  // `widgetPalette()` in the console, value for value.
  return dark ? '#17171a' : '#ffffff'
}

/**
 * The launcher's face: a bubble holding two sparks, and a cross once open.
 */
const ICON = 32

/** The ring of accent around the glyph. `PAD * 2 + ICON` is the launcher. */
const PAD = 10

/**
 * The mark is `currentColor`, like the label and the close cross beside it.
 */
const launcherSvg = (open: boolean) =>
  open
    ? `<svg width="${ICON}" height="${ICON}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`
    : `<svg width="${ICON}" height="${ICON}" viewBox="0 0 100 100" fill="none"><g transform="translate(0,-2)"><path d="M50 20C28 20 15 34 15 52c0 10 5 18 13 23q-3 7-8 11q12-2 20-9c3 1.5 6.5 2 10 2c22 0 35-14 35-27c0-18-13-32-35-32Z" fill="none" stroke="currentColor" stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/><path d="M42 35c0 10-8 14-14 16c6 2 14 6 14 16c0-10 8-14 14-16c-6-2-14-6-14-16Z" fill="currentColor"/><path d="M66 32c0 6-5 8-9 9c4 1 9 3 9 9c0-6 5-8 9-9c-4-1-9-3-9-9Z" fill="currentColor"/></g></svg>`

const mount = (config: Config) => {
  const edge = config.side === 'left' ? { left: `${EDGE}px` } : { right: `${EDGE}px` }

  /** What the console said, once it has arrived. Defaults until then. */
  let look: Presentation = {}

  // ---- launcher -----------------------------------------------------------

  /**
   * The launcher and its teaser share a row, and the row is what is positioned.
   */
  const row = document.createElement('div')
  style(row, {
    position: 'fixed',
    bottom: `${EDGE}px`,
    ...edge,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexDirection: config.side === 'left' ? 'row-reverse' : 'row',
    zIndex: '2147483646',
  })

  /**
   * The teaser: the sentence that gets somebody to press the button.
   */
  const teaser = document.createElement('button')
  teaser.type = 'button'
  style(teaser, {
    display: 'none',
    maxWidth: '176px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '6px 12px',
    border: '1px solid rgba(0,0,0,.09)',
    borderRadius: '16px',
    background: '#fff',
    color: '#101012',
    font: '400 12px/1.5 system-ui,-apple-system,sans-serif',
    boxShadow: '0 4px 12px rgba(0,0,0,.12)',
    cursor: 'pointer',
  })

  const launcher = document.createElement('button')
  launcher.type = 'button'
  launcher.setAttribute('aria-label', 'Чат нээх')
  launcher.setAttribute('aria-expanded', 'false')
  style(launcher, {
    position: 'relative',
    /**
     * On screen from the first frame, in the loading colours — see `dress`.
     */
    visibility: 'hidden',
    opacity: '0',
    transition: 'opacity .2s ease, background-color .2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    /**
     * 52px round — `PAD * 2 + ICON`.
     */
    padding: `${PAD}px`,
    border: 'none',
    borderRadius: '999px',
    background: DEFAULT_ACCENT,
    color: DEFAULT_ACCENT_INK,
    font: '500 13px/1 system-ui,-apple-system,sans-serif',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,.22)',
  })

  const badge = document.createElement('span')
  style(badge, {
    position: 'absolute',
    top: '0',
    ...(config.side === 'left' ? { left: '0' } : { right: '0' }),
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    background: '#D4402A',
    color: '#fff',
    font: '600 11px/18px system-ui,-apple-system,sans-serif',
    textAlign: 'center',
    display: 'none',
  })

  /**
   * The launcher's own loader, for the window before it has a face.
   */
  const pending = document.createElement('div')
  style(pending, {
    boxSizing: 'border-box',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    // Ink on grey, at the same weights `frame.css` gives `.spinner` — the
    // launcher's own colour is unknown at this point, which is the whole reason
    // this element exists.
    border: '2px solid rgba(0,0,0,.14)',
    borderTopColor: 'rgba(0,0,0,.45)',
  })

  /** Turning, unless the visitor has asked for less motion. Stopped by `dress`. */
  let pendingSpin: Animation | null = null

  /**
   * Whether the widget's own clothes have arrived — or been given up on.
   */
  let dressed = false

  /**
   * The two ends of the pulse: the default accent, and a shade under it.
   */
  const LOADING_FROM = DEFAULT_ACCENT
  const LOADING_TO = '#cfcfd6'

  /**
   * The launcher's face, redrawn on every state change.
   */
  const paintLauncher = (open: boolean) => {
    launcher.textContent = ''

    if (!dressed && !open) {
      // The skeleton: the ring, and the badge — which only exists after a reply
      // and therefore only after the config has long since landed.
      launcher.style.padding = `${PAD}px`
      launcher.style.width = `${PAD * 2 + ICON}px`
      launcher.style.height = `${PAD * 2 + ICON}px`
      launcher.style.justifyContent = 'center'
      launcher.appendChild(pending)
      launcher.appendChild(badge)
      return
    }

    // Back to a box that fits its contents — the skeleton is the only state
    // that pins a size, because a ring is not wide enough to make one.
    launcher.style.justifyContent = ''
    launcher.style.width = ''
    launcher.style.height = ''

    if (open) {
      launcher.insertAdjacentHTML('beforeend', launcherSvg(true))
    } else if (look.logo && /^(https:\/\/|data:image\/)/.test(look.logo)) {
      /**
       * The uploaded mark replaces the speech bubble, which is the whole point
       * of uploading one — a launcher wearing the customer's logo is recognised
       * before it is read. Built as an element with its `src` assigned rather
       * than as markup, so a URL can never become anything but a URL.
       */
      const mark = document.createElement('img')
      mark.src = look.logo
      mark.alt = ''
      style(mark, {
        width: `${ICON}px`,
        height: `${ICON}px`,
        objectFit: 'contain',
        display: 'block',
      })
      launcher.appendChild(mark)
    } else {
      launcher.insertAdjacentHTML('beforeend', launcherSvg(false))
    }

    if (!open && look.launcher === 'labeled' && look.launcherLabel) {
      const label = document.createElement('span')
      label.textContent = look.launcherLabel
      launcher.appendChild(label)
    }

    // A labelled pill is a shorter, wider shape than the round icon button.
    launcher.style.padding =
      !open && look.launcher === 'labeled' && look.launcherLabel ? '10px 16px' : `${PAD}px`

    launcher.appendChild(badge)
  }

  paintLauncher(false)
  row.append(teaser, launcher)

  // ---- frame --------------------------------------------------------------

  const frame = document.createElement('iframe')
  frame.title = 'Chat'
  frame.setAttribute('aria-hidden', 'true')
  /**
   * `allow` is empty and the sandbox is explicit rather than absent.
   */
  /**
   * `allow-popups` is what makes the footer link work.
   */
  frame.setAttribute(
    'sandbox',
    'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox',
  )
  style(frame, {
    position: 'fixed',
    /**
     * A hairline, and `border-box` with it. The preview draws one around the
     * panel and it is load-bearing on a white page: without it a white widget
     * on a white site is a shadow with nothing inside. The box model matters
     * because an iframe is content-box by default, so the border would
     * otherwise make the panel 370px and knock it out of alignment with the
     * launcher below.
     */
    boxSizing: 'border-box',
    border: '1px solid rgba(0,0,0,.09)',
    boxShadow: '0 12px 44px rgba(0,0,0,.24)',
    background: '#fff',
    zIndex: '2147483645',
    display: 'none',
    colorScheme: 'normal',
  })

  // ---- the wait ------------------------------------------------------------

  /**
   * The panel, before there is a panel.
   */
  const veil = document.createElement('div')
  veil.setAttribute('aria-hidden', 'true')
  style(veil, {
    position: 'fixed',
    boxSizing: 'border-box',
    display: 'none',
    placeItems: 'center',
    border: '1px solid rgba(0,0,0,.09)',
    boxShadow: '0 12px 44px rgba(0,0,0,.24)',
    background: '#fff',
    // The frame's own z-index; appended after it, so it paints on top of the
    // frame and under the launcher row.
    zIndex: '2147483645',
    opacity: '1',
    transition: 'opacity .16s ease',
  })

  const ring = document.createElement('div')
  style(ring, {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    // Recoloured for a dark widget by `applyPresentation`, along with the
    // surface it sits on.
    border: '2px solid rgba(0,0,0,.09)',
    borderTopColor: 'rgba(0,0,0,.42)',
  })
  veil.appendChild(ring)

  document.body.appendChild(frame)
  document.body.appendChild(veil)
  document.body.appendChild(row)

  // ---- geometry -----------------------------------------------------------

  /**
   * Where the panel goes, given the viewport and the launcher's current shape.
   */
  const layout = () => {
    const phone = window.innerWidth <= PHONE
    const radius = typeof look.radius === 'number' ? look.radius : 16

    /**
     * One box, applied to two elements. The veil is the panel's stand-in and has
     * to be the panel's exact shape — a loader an eighth of a radius out from
     * the thing it becomes is a visible jolt at the handover, and keeping two
     * copies of this arithmetic in step by hand is how that happens.
     */
    const box: Partial<CSSStyleDeclaration> = phone
      ? {
          inset: '0',
          width: '100%',
          height: '100%',
          maxWidth: 'none',
          borderRadius: '0',
          // A sheet has no edge to draw: the border and the shadow both exist to
          // separate a panel from the page behind it, and there is no page left.
          borderWidth: '0',
          boxShadow: 'none',
        }
      : {
          inset: 'auto',
          // Clear of the launcher row, whatever shape it currently is.
          bottom: `${EDGE + row.offsetHeight + 8}px`,
          ...edge,
          width: `${PANEL_WIDTH}px`,
          height: `min(${PANEL_HEIGHT}px, calc(100vh - ${EDGE * 2 + row.offsetHeight + 8 + 20}px))`,
          maxWidth: `calc(100vw - ${EDGE * 2}px)`,
          borderRadius: `${radius}px`,
          borderWidth: '1px',
          boxShadow: '0 12px 44px rgba(0,0,0,.24)',
        }

    style(frame, box)
    style(veil, box)

    // The launcher would sit on top of a full-screen sheet; the frame draws its
    // own close button, so nothing is lost by hiding it.
    row.style.display = phone && open ? 'none' : 'flex'
  }

  window.addEventListener('resize', layout)

  // ---- state --------------------------------------------------------------

  let open = false
  let loaded = false
  /** Whether the visitor has had a say — after which nothing opens by itself. */
  let touched = false
  /** Whether the frame has said it is on screen — see the `ready` message. */
  let painted = false

  // Size and place the panel once up front, so a resize before the first open
  // has something to correct rather than something to invent.
  layout()

  const post = (message: LoaderMessage) => frame.contentWindow?.postMessage(message, FRAME_ORIGIN)

  /**
   * The ring turns only while it is being looked at.
   */
  let spin: Animation | null = null
  let hide = 0

  const showVeil = () => {
    window.clearTimeout(hide)
    veil.style.display = 'grid'
    veil.style.opacity = '1'

    if (!spin && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      spin =
        ring.animate?.([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], {
          duration: 700,
          iterations: Infinity,
          easing: 'linear',
        }) ?? null
    }
  }

  /**
   * Hand the wait over to the frame.
   */
  const dropVeil = () => {
    if (veil.style.display === 'none') return
    veil.style.opacity = '0'
    hide = window.setTimeout(() => {
      veil.style.display = 'none'
    }, 200)
  }

  const setOpen = (next: boolean) => {
    /**
     * Pressed before the config arrived: the wait is over whether or not the
     * answer is, because the panel is now the thing being looked at and a
     * launcher still shimmering under it is announcing a wait nobody is in.
     */
    dress()

    open = next
    launcher.setAttribute('aria-expanded', String(next))
    launcher.setAttribute('aria-label', next ? 'Чат хаах' : 'Чат нээх')
    paintLauncher(next)
    frame.style.display = next ? 'block' : 'none'
    frame.setAttribute('aria-hidden', String(!next))

    /**
     * The ring goes up with the panel and comes down with it — and only while
     * there is genuinely nothing behind it. Once the frame has painted it never
     * returns: a second open reuses the loaded document, which is instant, and a
     * loader over an instant thing is a flicker.
     */
    if (next && !painted) showVeil()
    else if (!next) {
      window.clearTimeout(hide)
      veil.style.display = 'none'
    }

    // The teaser has done its job the moment the panel has been open once.
    if (next) teaser.style.display = 'none'

    if (next) {
      /**
       * The src is set on first open, never at mount.
       */
      if (!loaded) {
        /**
         * `/frame.html`, not `/v1/frame`.
         */
        const url = new URL('/frame.html', FRAME_ORIGIN)
        url.searchParams.set('k', config.publishableKey)
        url.searchParams.set('o', window.location.origin)
        url.searchParams.set('side', config.side)
        if (config.agentId) url.searchParams.set('a', config.agentId)
        /**
         * Truncated rather than sent whole. The frame's `src` is a URL and a
         * URL has a practical length limit, so an id long enough to threaten it
         * would break the *panel* rather than the identification — a failure
         * wildly out of proportion to its cause. 120 is comfortably past any
         * real account id, and it is `handle`'s limit on the session endpoint —
         * so a longer one is refused there anyway, and cutting it here turns a
         * 400 nobody would understand into an id that simply identifies less.
         */
        if (config.userId) url.searchParams.set('u', config.userId.slice(0, 120))
        /**
         * The theme travels with the URL because this file already knows it and
         * the frame is about to spend a request finding out. Without it the frame
         * paints its default light panel first and turns dark when its own config
         * lands — a flash one step further down the same road this ring exists to
         * pave over. Resolved to a concrete value here: `system` is the frame's
         * own default, and it and this file are on the same device, so they read
         * the same preference.
         */
        url.searchParams.set('theme', surfaceFor(look.theme) === '#ffffff' ? 'light' : 'dark')
        frame.src = url.toString()
        loaded = true

        /**
         * A frame that never arrives must not spin for ever.
         */
        window.setTimeout(dropVeil, 12_000)
      } else {
        post({ tag: TAG, type: 'open' })
      }
      badge.style.display = 'none'
      badge.textContent = ''
    } else if (loaded) {
      post({ tag: TAG, type: 'shut' })
    }

    layout()
  }

  launcher.addEventListener('click', () => {
    touched = true
    setOpen(!open)
  })

  teaser.addEventListener('click', () => {
    touched = true
    setOpen(true)
  })

  window.addEventListener('message', (event) => {
    // Origin first: the tag is ours to choose and therefore anyone's to copy.
    if (event.origin !== FRAME_ORIGIN) return
    if (event.source !== frame.contentWindow) return
    if (!isFrameMessage(event.data)) return

    switch (event.data.type) {
      case 'close':
        touched = true
        setOpen(false)
        break
      case 'unread':
        if (!open && event.data.count > 0) {
          badge.textContent = String(Math.min(event.data.count, 9))
          badge.style.display = 'block'
        }
        break
      case 'accent':
        /**
         * The frame's own copy of the accent, and the reason to keep it now
         * that this file fetches the config itself: it is the fallback for the
         * one case that fetch can fail and the frame's cannot — a network blip,
         * an ad blocker with an opinion about cross-origin JSON. Validated
         * rather than trusted: the value is written into a style property, and
         * the frame is ours but the message channel is not.
         */
        if (/^#[0-9a-fA-F]{6}$/.test(event.data.accent) && !look.accent) {
          look = { ...look, accent: event.data.accent }
          launcher.style.background = event.data.accent
          launcher.style.color = readableOn(event.data.accent)
        }
        break
      case 'ready':
        /**
         * The frame's document has run its script, which is where its own ring
         * is already in the markup and its theme is already stamped — so there
         * is something behind the veil now and it is the right colour.
         */
        painted = true
        requestAnimationFrame(() => requestAnimationFrame(dropVeil))
        break
    }
  })

  // Escape closes the panel from the host page too — the frame handles its own,
  // but focus is often still outside it right after opening.
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && open) {
      touched = true
      setOpen(false)
    }
  })

  // ---- the customer's design ----------------------------------------------

  /**
   * Once per tab, per widget.
   */
  const AUTO_OPEN_KEY = `raptora.autoopen:${config.publishableKey}`

  const spent = () => {
    try {
      return sessionStorage.getItem(AUTO_OPEN_KEY) === '1'
    } catch {
      // Private mode, or a site that blocks storage. Treat it as spent: opening
      // by itself on every page load is the failure worth avoiding.
      return true
    }
  }

  const spend = () => {
    try {
      sessionStorage.setItem(AUTO_OPEN_KEY, '1')
    } catch {
      /* nothing to do — see above */
    }
  }

  const autoOpen = () => {
    if (open || touched || spent()) return
    spend()
    setOpen(true)
  }

  /**
   * Put the launcher on screen, in whatever state it is currently in.
   */
  let shown = false

  const reveal = () => {
    if (shown) return
    shown = true
    launcher.style.visibility = 'visible'
    launcher.style.opacity = '1'
    layout()
  }

  /**
   * The pulse between the default accent and a light grey.
   */
  let shimmer: Animation | null = null

  const startShimmer = () => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      // Still a placeholder, simply not a moving one — and the deeper grey, so
      // a visitor who has asked for less motion is looking at a button that is
      // visibly still waiting rather than at one already in its final colour.
      // The ring stays too, still rather than absent: it is what says
      // *waiting*, and a ring that does not turn still says it.
      launcher.style.background = LOADING_TO
      return
    }

    pendingSpin =
      pending.animate?.([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], {
        duration: 700,
        iterations: Infinity,
        easing: 'linear',
      }) ?? null

    shimmer =
      launcher.animate?.(
        [
          { backgroundColor: LOADING_FROM },
          { backgroundColor: LOADING_TO },
          { backgroundColor: LOADING_FROM },
        ],
        { duration: 1400, iterations: Infinity, easing: 'ease-in-out' },
      ) ?? null
  }

  /**
   * Stop pretending to load, and wear whatever we ended up with.
   */
  const dress = () => {
    if (dressed) return
    dressed = true
    shimmer?.cancel()
    shimmer = null
    // The ring is removed by the `paintLauncher` below; cancelling here is what
    // stops it costing a frame a second for the rest of the page's life.
    pendingSpin?.cancel()
    pendingSpin = null

    // Nothing usable arrived, so the default grey it is — a launcher left on the
    // deeper placeholder shade would be a permanent loading state.
    if (!look.accent || !/^#[0-9a-fA-F]{6}$/.test(look.accent)) {
      launcher.style.background = DEFAULT_ACCENT
      launcher.style.color = DEFAULT_ACCENT_INK
    }

    paintLauncher(open)
    reveal()
  }

  const applyPresentation = (next: Presentation) => {
    look = next

    if (next.accent && /^#[0-9a-fA-F]{6}$/.test(next.accent)) {
      launcher.style.background = next.accent
      launcher.style.color = readableOn(next.accent)
    }

    const surface = surfaceFor(next.theme)
    const rule = surface === '#ffffff' ? 'rgba(0,0,0,.09)' : 'rgba(255,255,255,.11)'
    const ink = surface === '#ffffff' ? '#101012' : '#f5f5f6'

    frame.style.background = surface
    frame.style.borderColor = rule

    /**
     * The veil is the panel until the panel exists, so it wears the panel's
     * clothes: the customer's surface, their corner, their hairline — and a ring
     * in the same greys `frame.css` gives `.spinner` from `--rule` and `--muted`,
     * so the handover is a cross-fade between two identical objects rather than
     * a change of subject.
     */
    const dark = surface !== '#ffffff'
    veil.style.background = surface
    veil.style.borderColor = rule
    ring.style.borderColor = rule
    ring.style.borderTopColor = dark ? '#9a9aa3' : '#6b6b73'
    style(teaser, {
      background: surface,
      color: ink,
      border: `1px solid ${rule}`,
      // Bubbles cap at 18, as everywhere else the radius is applied to one.
      borderRadius: `${Math.min(typeof next.radius === 'number' ? next.radius : 16, 18)}px`,
    })

    dress()

    if (next.autoOpen && next.autoOpen !== 'never') {
      if (next.teaser && !open) {
        teaser.textContent = next.teaser
        teaser.style.display = 'block'
        layout()
      }

      if (next.autoOpen === 'delay') {
        window.setTimeout(autoOpen, Math.max(next.autoOpenDelaySec ?? 15, 1) * 1000)
      } else {
        /**
         * Exit intent: the pointer leaving through the top of the document is
         * the closest a browser gets to "they are going for the address bar".
         * Desktop only by nature — a touch device has no pointer to lose — and
         * deliberately not replaced with a scroll or a timer there, because the
         * alternative to a good signal is not a bad signal, it is none.
         */
        document.addEventListener(
          'mouseout',
          (event) => {
            if (!event.relatedTarget && event.clientY <= 0) autoOpen()
          },
          { passive: true },
        )
      }
    }
  }

  /**
   * Failing silently, and never leaving the launcher shimmering.
   */
  const loadPresentation = async () => {
    try {
      const url = new URL('/v1/widget/config', API)
      url.searchParams.set('key', config.publishableKey)
      url.searchParams.set('origin', window.location.origin)
      if (config.agentId) url.searchParams.set('agentId', config.agentId)

      const response = await fetch(url.toString())
      if (!response.ok) return

      const body = (await response.json()) as { config: Presentation | null }
      if (body.config) applyPresentation(body.config)
    } catch {
      /* the widget still works; it just looks like everybody else's */
    } finally {
      dress()
    }
  }

  /**
   * Started now, on page load, rather than deferred to idle.
   */
  reveal()
  startShimmer()
  window.setTimeout(dress, 5000)
  void loadPresentation()
}

/**
 * `document.currentScript` is only valid during synchronous execution, so the
 * config must be read now even though mounting waits for `body`.
 */
const config = readConfig()

if (config && !document.documentElement.hasAttribute(INSTALLED)) {
  document.documentElement.setAttribute(INSTALLED, '')

  if (document.body) {
    mount(config)
  } else {
    document.addEventListener('DOMContentLoaded', () => mount(config), {
      once: true,
    })
  }
}

export {}
